import { useState, useRef } from "react";
import { motion } from "framer-motion";
import { Printer, FileText, Plus, Trash2, Download, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_c42ba4e9-9f23-42d1-8dca-60d6e029ce6f/artifacts/b4bmdmcp_IMG_7020.jpeg";
const ACCESS_CODE = "masb2024";

const defaultLineItems = [{ description: "", qty: 1, rate: 0 }];

// ─── Web Services Agreement (static) ───
const WEB_AGREEMENT = {
  contractor: "Kevin Bradshaw",
  company: "Bradshaw Solutions",
  client: "Andrew Jacob",
  clientCompany: "Mid America Star Builders LLC",
  website: "MidAmericaStarBuilders.com",
  term: "12 months",
  fee: "$200.00",
  date: new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" }),
};

// ─── Shared Line Items Component ───
function LineItemsEditor({ lineItems, setLineItems }) {
  const addLineItem = () => {
    setLineItems([...lineItems, { description: "", qty: 1, rate: 0 }]);
  };
  const removeLineItem = (index) => {
    if (lineItems.length > 1) setLineItems(lineItems.filter((_, i) => i !== index));
  };
  const updateLineItem = (index, field, value) => {
    const updated = [...lineItems];
    updated[index] = { ...updated[index], [field]: field === "description" ? value : Number(value) };
    setLineItems(updated);
  };

  return (
    <div>
      <h3 className="font-outfit font-bold text-lg uppercase tracking-wider text-brand-dark mb-4">Line Items</h3>
      <div className="space-y-3">
        <div className="grid grid-cols-12 gap-2 text-xs font-outfit font-bold uppercase tracking-wider text-neutral-500">
          <div className="col-span-6">Description</div>
          <div className="col-span-2">Qty</div>
          <div className="col-span-3">Rate ($)</div>
          <div className="col-span-1"></div>
        </div>
        {lineItems.map((item, i) => (
          <div key={i} className="grid grid-cols-12 gap-2 items-center">
            <div className="col-span-6">
              <Input value={item.description} onChange={(e) => updateLineItem(i, "description", e.target.value)} placeholder="Service or material" className="rounded-none border-2 border-neutral-300 font-dm-sans text-sm" />
            </div>
            <div className="col-span-2">
              <Input type="number" min="1" value={item.qty} onChange={(e) => updateLineItem(i, "qty", e.target.value)} className="rounded-none border-2 border-neutral-300 font-dm-sans text-sm" />
            </div>
            <div className="col-span-3">
              <Input type="number" min="0" step="0.01" value={item.rate} onChange={(e) => updateLineItem(i, "rate", e.target.value)} className="rounded-none border-2 border-neutral-300 font-dm-sans text-sm" />
            </div>
            <div className="col-span-1 flex justify-center">
              <button onClick={() => removeLineItem(i)} className="text-neutral-400 hover:text-brand transition-colors"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
      </div>
      <Button onClick={addLineItem} variant="outline" className="mt-4 rounded-none border-2 border-neutral-300 font-outfit font-bold text-xs uppercase tracking-wider">
        <Plus className="w-4 h-4 mr-2" /> Add Line Item
      </Button>
    </div>
  );
}

// ─── Client Info Form ───
function ClientInfoForm({ client, setClient }) {
  return (
    <div>
      <h3 className="font-outfit font-bold text-lg uppercase tracking-wider text-brand-dark mb-4">Client Information</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="font-outfit font-semibold text-xs uppercase tracking-wider">Client Name *</Label>
          <Input value={client.name} onChange={(e) => setClient({ ...client, name: e.target.value })} placeholder="Full name" className="rounded-none border-2 border-neutral-300 font-dm-sans" />
        </div>
        <div className="space-y-2">
          <Label className="font-outfit font-semibold text-xs uppercase tracking-wider">Phone</Label>
          <Input value={client.phone} onChange={(e) => setClient({ ...client, phone: e.target.value })} placeholder="(901) 000-0000" className="rounded-none border-2 border-neutral-300 font-dm-sans" />
        </div>
        <div className="space-y-2">
          <Label className="font-outfit font-semibold text-xs uppercase tracking-wider">Address</Label>
          <Input value={client.address} onChange={(e) => setClient({ ...client, address: e.target.value })} placeholder="Street address" className="rounded-none border-2 border-neutral-300 font-dm-sans" />
        </div>
        <div className="space-y-2">
          <Label className="font-outfit font-semibold text-xs uppercase tracking-wider">Email</Label>
          <Input value={client.email} onChange={(e) => setClient({ ...client, email: e.target.value })} placeholder="email@example.com" className="rounded-none border-2 border-neutral-300 font-dm-sans" />
        </div>
        <div className="space-y-2">
          <Label className="font-outfit font-semibold text-xs uppercase tracking-wider">City</Label>
          <Input value={client.city} onChange={(e) => setClient({ ...client, city: e.target.value })} placeholder="Memphis" className="rounded-none border-2 border-neutral-300 font-dm-sans" />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="font-outfit font-semibold text-xs uppercase tracking-wider">State</Label>
            <Input value={client.state} onChange={(e) => setClient({ ...client, state: e.target.value })} className="rounded-none border-2 border-neutral-300 font-dm-sans" />
          </div>
          <div className="space-y-2">
            <Label className="font-outfit font-semibold text-xs uppercase tracking-wider">ZIP</Label>
            <Input value={client.zip} onChange={(e) => setClient({ ...client, zip: e.target.value })} placeholder="38101" className="rounded-none border-2 border-neutral-300 font-dm-sans" />
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Main Page Component ───
export default function ContractPage() {
  const [authenticated, setAuthenticated] = useState(false);
  const [codeInput, setCodeInput] = useState("");
  const [codeError, setCodeError] = useState(false);

  // Contract state
  const [client, setClient] = useState({ name: "", address: "", city: "", state: "TN", zip: "", phone: "", email: "" });
  const [project, setProject] = useState({ description: "", address: "", startDate: "", estimatedCompletion: "" });
  const [contractItems, setContractItems] = useState([...defaultLineItems]);
  const [contractNotes, setContractNotes] = useState("");

  // Invoice state
  const [invClient, setInvClient] = useState({ name: "", address: "", city: "", state: "TN", zip: "", phone: "", email: "" });
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [invoiceDate, setInvoiceDate] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [invoiceItems, setInvoiceItems] = useState([...defaultLineItems]);
  const [invoiceNotes, setInvoiceNotes] = useState("");

  const contractSubtotal = contractItems.reduce((sum, item) => sum + item.qty * item.rate, 0);
  const depositAmount = contractSubtotal / 3;
  const invoiceSubtotal = invoiceItems.reduce((sum, item) => sum + item.qty * item.rate, 0);

  const today = new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });

  const handleLogin = (e) => {
    e.preventDefault();
    if (codeInput === ACCESS_CODE) {
      setAuthenticated(true);
      setCodeError(false);
    } else {
      setCodeError(true);
    }
  };

  const handlePrint = () => window.print();

  const handleDownloadExcel = () => {
    const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
    window.open(`${API}/contract-template`, "_blank");
  };

  // ─── LOGIN SCREEN ───
  if (!authenticated) {
    return (
      <div data-testid="contract-login" className="min-h-screen flex items-center justify-center bg-neutral-50">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-white border-2 border-neutral-200 p-10 w-full max-w-sm text-center">
          <div className="w-16 h-16 bg-brand-dark flex items-center justify-center mx-auto mb-6">
            <Lock className="w-8 h-8 text-white" />
          </div>
          <h1 className="font-outfit text-2xl font-black uppercase tracking-tight text-brand-dark mb-2">Admin Access</h1>
          <p className="font-dm-sans text-sm text-neutral-500 mb-6">Enter your access code to continue</p>
          <form onSubmit={handleLogin} className="space-y-4">
            <Input
              type="password"
              value={codeInput}
              onChange={(e) => { setCodeInput(e.target.value); setCodeError(false); }}
              placeholder="Access code"
              data-testid="access-code-input"
              className={`rounded-none border-2 font-dm-sans text-center text-lg py-5 ${codeError ? "border-brand" : "border-neutral-300"}`}
            />
            {codeError && <p className="font-dm-sans text-sm text-brand">Incorrect access code</p>}
            <Button type="submit" data-testid="access-code-submit" className="w-full bg-brand text-white font-outfit font-bold uppercase tracking-wider text-sm py-5 hover:bg-brand-hover rounded-none">
              Enter
            </Button>
          </form>
        </motion.div>
      </div>
    );
  }

  // ─── AUTHENTICATED VIEW ───
  return (
    <div data-testid="contract-page">
      {/* Header */}
      <section className="bg-brand-dark py-16 md:py-20 print:hidden">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <span className="font-outfit text-sm font-bold tracking-[0.2em] uppercase text-brand block mb-3">Business Tools</span>
            <h1 className="font-outfit text-4xl sm:text-5xl font-black tracking-tighter uppercase text-white">Contracts & Invoices</h1>
            <p className="font-dm-sans text-neutral-400 text-base mt-4 max-w-lg">Fill in details, preview, and print professional documents with all protective clauses.</p>
          </motion.div>
        </div>
      </section>

      {/* Tabs: Contract / Invoice */}
      <section className="py-12 bg-neutral-50 print:hidden">
        <div className="max-w-5xl mx-auto px-4 md:px-8">
          <Tabs defaultValue="contract" className="w-full">
            <TabsList className="w-full grid grid-cols-2 mb-8 rounded-none h-12 bg-neutral-200">
              <TabsTrigger value="contract" className="rounded-none font-outfit font-bold uppercase tracking-wider text-sm data-[state=active]:bg-brand data-[state=active]:text-white">Contract</TabsTrigger>
              <TabsTrigger value="invoice" className="rounded-none font-outfit font-bold uppercase tracking-wider text-sm data-[state=active]:bg-brand data-[state=active]:text-white">Invoice</TabsTrigger>
            </TabsList>

            {/* ═══ CONTRACT TAB ═══ */}
            <TabsContent value="contract">
              <div className="bg-white border border-neutral-200 p-8 space-y-8">
                <ClientInfoForm client={client} setClient={setClient} />
                <Separator />
                <div>
                  <h3 className="font-outfit font-bold text-lg uppercase tracking-wider text-brand-dark mb-4">Project Details</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="sm:col-span-2 space-y-2">
                      <Label className="font-outfit font-semibold text-xs uppercase tracking-wider">Project Description *</Label>
                      <Textarea value={project.description} onChange={(e) => setProject({ ...project, description: e.target.value })} placeholder="Describe the scope of work..." rows={3} className="rounded-none border-2 border-neutral-300 font-dm-sans resize-none" />
                    </div>
                    <div className="space-y-2">
                      <Label className="font-outfit font-semibold text-xs uppercase tracking-wider">Project Address</Label>
                      <Input value={project.address} onChange={(e) => setProject({ ...project, address: e.target.value })} placeholder="If different from client address" className="rounded-none border-2 border-neutral-300 font-dm-sans" />
                    </div>
                    <div className="space-y-2">
                      <Label className="font-outfit font-semibold text-xs uppercase tracking-wider">Start Date</Label>
                      <Input type="date" value={project.startDate} onChange={(e) => setProject({ ...project, startDate: e.target.value })} className="rounded-none border-2 border-neutral-300 font-dm-sans" />
                    </div>
                    <div className="space-y-2">
                      <Label className="font-outfit font-semibold text-xs uppercase tracking-wider">Estimated Completion</Label>
                      <Input type="date" value={project.estimatedCompletion} onChange={(e) => setProject({ ...project, estimatedCompletion: e.target.value })} className="rounded-none border-2 border-neutral-300 font-dm-sans" />
                    </div>
                  </div>
                </div>
                <Separator />
                <LineItemsEditor lineItems={contractItems} setLineItems={setContractItems} />
                <Separator />
                <div className="space-y-2">
                  <Label className="font-outfit font-semibold text-xs uppercase tracking-wider">Additional Notes</Label>
                  <Textarea value={contractNotes} onChange={(e) => setContractNotes(e.target.value)} placeholder="Any additional project notes..." rows={2} className="rounded-none border-2 border-neutral-300 font-dm-sans resize-none" />
                </div>
                <div className="flex flex-wrap gap-4">
                  <Button onClick={handlePrint} data-testid="print-contract-btn" className="bg-brand text-white font-outfit font-bold uppercase tracking-wider text-sm px-8 py-5 hover:bg-brand-hover rounded-none">
                    <Printer className="w-4 h-4 mr-2" /> Print Contract
                  </Button>
                  <Button onClick={handleDownloadExcel} data-testid="download-excel-btn" className="bg-brand-dark text-white font-outfit font-bold uppercase tracking-wider text-sm px-8 py-5 hover:bg-neutral-800 rounded-none">
                    <Download className="w-4 h-4 mr-2" /> Download Excel
                  </Button>
                </div>
              </div>

              {/* Contract Preview */}
              <div className="mt-10">
                <h2 className="font-outfit text-2xl font-bold tracking-tight uppercase text-brand-dark mb-6 flex items-center gap-3">
                  <FileText className="w-6 h-6 text-brand" /> Contract Preview
                </h2>
                <div className="border-2 border-neutral-200 bg-white p-8 shadow-lg text-sm leading-relaxed font-dm-sans">
                  <div className="flex justify-between items-start border-b-4 border-black pb-4 mb-6">
                    <div className="flex items-center gap-3">
                      <img src={LOGO_URL} alt="MASB" className="h-14 w-auto" />
                      <div>
                        <h1 className="font-outfit text-xl font-black tracking-tight uppercase">Mid America Star Builders LLC</h1>
                        <p className="text-xs text-neutral-500">Professional General Contractor — Memphis, TN</p>
                        <p className="text-xs">Phone: (901) 406-1803 | Email: Masb254@yahoo.com</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <h2 className="font-outfit text-2xl font-black uppercase text-brand">CONTRACT</h2>
                      <p className="text-xs text-neutral-500">Date: {today}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-8 mb-6 text-sm">
                    <div>
                      <h3 className="font-outfit font-bold uppercase text-xs tracking-wider border-b border-neutral-300 pb-1 mb-2">Bill To</h3>
                      <p className="font-semibold">{client.name || "________________________"}</p>
                      <p>{client.address || "________________________"}</p>
                      <p>{client.city || "________"}, {client.state} {client.zip || "_____"}</p>
                      <p>Phone: {client.phone || "________________________"}</p>
                    </div>
                    <div>
                      <h3 className="font-outfit font-bold uppercase text-xs tracking-wider border-b border-neutral-300 pb-1 mb-2">Project</h3>
                      <p><span className="font-semibold">Location:</span> {project.address || client.address || "Same as billing"}</p>
                      <p><span className="font-semibold">Start:</span> {project.startDate || "Upon deposit"}</p>
                      <p><span className="font-semibold">Est. Completion:</span> {project.estimatedCompletion || "TBD"}</p>
                    </div>
                  </div>
                  {project.description && (
                    <div className="mb-6">
                      <h3 className="font-outfit font-bold uppercase text-xs tracking-wider border-b border-neutral-300 pb-1 mb-2">Scope of Work</h3>
                      <p>{project.description}</p>
                    </div>
                  )}
                  <table className="w-full mb-6 text-sm">
                    <thead>
                      <tr className="bg-brand-dark text-white text-xs">
                        <th className="text-left p-2 font-outfit font-bold uppercase tracking-wider">Description</th>
                        <th className="text-center p-2 w-16 font-outfit font-bold uppercase tracking-wider">Qty</th>
                        <th className="text-right p-2 w-24 font-outfit font-bold uppercase tracking-wider">Rate</th>
                        <th className="text-right p-2 w-24 font-outfit font-bold uppercase tracking-wider">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {contractItems.map((item, i) => (
                        <tr key={i} className="border-b border-neutral-200">
                          <td className="p-2">{item.description || "—"}</td>
                          <td className="p-2 text-center">{item.qty}</td>
                          <td className="p-2 text-right">${item.rate.toFixed(2)}</td>
                          <td className="p-2 text-right font-semibold">${(item.qty * item.rate).toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="border-t-2 border-black">
                        <td colSpan="3" className="p-2 text-right font-outfit font-bold uppercase">Total:</td>
                        <td className="p-2 text-right font-outfit font-bold text-lg">${contractSubtotal.toFixed(2)}</td>
                      </tr>
                      <tr>
                        <td colSpan="3" className="p-2 text-right font-semibold text-brand">Required Deposit (1/3):</td>
                        <td className="p-2 text-right font-bold text-brand">${depositAmount.toFixed(2)}</td>
                      </tr>
                    </tfoot>
                  </table>
                  <div className="border-2 border-brand-dark p-4 mb-6">
                    <h3 className="font-outfit font-black uppercase text-sm tracking-wider text-center mb-3 text-brand">Terms and Conditions</h3>
                    <div className="space-y-2 text-xs">
                      <p><span className="font-bold">1. DEPOSIT AND COMMENCEMENT OF WORK:</span> All work under this Contract shall commence only upon receipt of a deposit equal to one-third (1/3) of the total contract amount. No materials shall be ordered, no labor shall be scheduled, and no work of any kind shall begin until said deposit has been received and cleared by Mid America Star Builders LLC ("Contractor").</p>
                      <p><span className="font-bold">2. COMPLETION BEFORE NEW WORK:</span> Once work under this Contract has commenced, the Contractor shall complete all work described herein before beginning any new contract, project, or scope of work for the same Client. The Client acknowledges and agrees that the Contractor must finish the current project in its entirety before any additional or separate work may be started.</p>
                      <p><span className="font-bold">3. PAYMENT TERMS AND LATE FEE:</span> The remaining balance of the contract amount shall be due upon completion of work unless otherwise agreed in writing. In the event of non-payment or late payment, a late fee of One Hundred Dollars ($100.00) per day shall accrue for each calendar day that payment remains outstanding past the due date. This daily late fee shall continue to accumulate until the full balance, including all accrued late fees, is paid in full.</p>
                      <p><span className="font-bold">4. LEGAL FEES AND COURT COSTS — INDEMNIFICATION:</span> In the event that any legal action, lawsuit, claim, or proceeding is initiated by the Client against Mid America Star Builders LLC, its owner Andrew Jacob, its employees, agents, or subcontractors, the Client agrees to be solely responsible for and shall pay all court costs, filing fees, attorney's fees, legal expenses, and any other costs associated with such action, regardless of the outcome. This includes but is not limited to costs incurred by the Contractor in defending against any claims, counterclaims, or cross-claims. The Client hereby waives any right to seek reimbursement of legal fees from the Contractor.</p>
                      <p><span className="font-bold">5. DISPUTE RESOLUTION:</span> Prior to initiating any legal action, both parties agree to attempt to resolve disputes through good-faith negotiation. If a resolution cannot be reached, any legal proceedings shall be conducted in the courts of Shelby County, Tennessee, and shall be governed by the laws of the State of Tennessee.</p>
                      <p><span className="font-bold">6. CHANGE ORDERS:</span> Any changes to the scope of work described in this Contract must be agreed upon in writing by both parties. Additional work may result in additional charges and an adjusted timeline.</p>
                      <p><span className="font-bold">7. WARRANTY:</span> Contractor warrants all workmanship for a period of one (1) year from the date of completion. This warranty does not cover damage caused by the Client, natural disasters, or normal wear and tear.</p>
                      <p><span className="font-bold">8. CANCELLATION:</span> If the Client cancels this Contract after work has commenced, the Client shall be responsible for 20% of the contract and all work completed to date, materials ordered or delivered, and any other cancellation fees. The deposit is non-refundable once work has begun.</p>
                      <p><span className="font-bold">9. LIABILITY LIMITATION:</span> The Contractor's total liability under this Contract shall not exceed the total contract amount. The Contractor shall not be liable for any indirect, incidental, or consequential damages.</p>
                      <p><span className="font-bold">10. ENTIRE AGREEMENT:</span> This Contract constitutes the entire agreement between the parties and supersedes all prior negotiations, representations, or agreements. No modification of this Contract shall be valid unless made in writing and signed by both parties.</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-12 mt-8">
                    <div>
                      <p className="font-outfit font-bold uppercase text-xs tracking-wider mb-8">Contractor</p>
                      <div className="border-b border-black mb-1"></div>
                      <p className="text-xs text-neutral-500">Mario Jacob — Mid America Star Builders LLC</p>
                    </div>
                    <div>
                      <p className="font-outfit font-bold uppercase text-xs tracking-wider mb-8">Client</p>
                      <div className="border-b border-black mb-1"></div>
                      <p className="text-xs text-neutral-500">{client.name || "Client"} — Signature</p>
                    </div>
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* ═══ INVOICE TAB ═══ */}
            <TabsContent value="invoice">
              <div className="bg-white border border-neutral-200 p-8 space-y-8">
                <ClientInfoForm client={invClient} setClient={setInvClient} />
                <Separator />
                <div>
                  <h3 className="font-outfit font-bold text-lg uppercase tracking-wider text-brand-dark mb-4">Invoice Details</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label className="font-outfit font-semibold text-xs uppercase tracking-wider">Invoice #</Label>
                      <Input value={invoiceNumber} onChange={(e) => setInvoiceNumber(e.target.value)} placeholder="INV-001" className="rounded-none border-2 border-neutral-300 font-dm-sans" />
                    </div>
                    <div className="space-y-2">
                      <Label className="font-outfit font-semibold text-xs uppercase tracking-wider">Invoice Date</Label>
                      <Input type="date" value={invoiceDate} onChange={(e) => setInvoiceDate(e.target.value)} className="rounded-none border-2 border-neutral-300 font-dm-sans" />
                    </div>
                    <div className="space-y-2">
                      <Label className="font-outfit font-semibold text-xs uppercase tracking-wider">Due Date</Label>
                      <Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} className="rounded-none border-2 border-neutral-300 font-dm-sans" />
                    </div>
                  </div>
                </div>
                <Separator />
                <LineItemsEditor lineItems={invoiceItems} setLineItems={setInvoiceItems} />
                <Separator />
                <div className="space-y-2">
                  <Label className="font-outfit font-semibold text-xs uppercase tracking-wider">Notes</Label>
                  <Textarea value={invoiceNotes} onChange={(e) => setInvoiceNotes(e.target.value)} placeholder="Payment instructions or notes..." rows={2} className="rounded-none border-2 border-neutral-300 font-dm-sans resize-none" />
                </div>
                <div className="flex flex-wrap gap-4">
                  <Button onClick={handlePrint} className="bg-brand text-white font-outfit font-bold uppercase tracking-wider text-sm px-8 py-5 hover:bg-brand-hover rounded-none">
                    <Printer className="w-4 h-4 mr-2" /> Print Invoice
                  </Button>
                  <Button onClick={handleDownloadExcel} className="bg-brand-dark text-white font-outfit font-bold uppercase tracking-wider text-sm px-8 py-5 hover:bg-neutral-800 rounded-none">
                    <Download className="w-4 h-4 mr-2" /> Download Excel
                  </Button>
                </div>
              </div>

              {/* Invoice Preview */}
              <div className="mt-10">
                <h2 className="font-outfit text-2xl font-bold tracking-tight uppercase text-brand-dark mb-6 flex items-center gap-3">
                  <FileText className="w-6 h-6 text-brand" /> Invoice Preview
                </h2>
                <div className="border-2 border-neutral-200 bg-white p-8 shadow-lg text-sm leading-relaxed font-dm-sans">
                  <div className="flex justify-between items-start border-b-4 border-black pb-4 mb-6">
                    <div className="flex items-center gap-3">
                      <img src={LOGO_URL} alt="MASB" className="h-14 w-auto" />
                      <div>
                        <h1 className="font-outfit text-xl font-black tracking-tight uppercase">Mid America Star Builders LLC</h1>
                        <p className="text-xs text-neutral-500">Professional General Contractor — Memphis, TN</p>
                        <p className="text-xs">Phone: (901) 406-1803 | Email: Masb254@yahoo.com</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <h2 className="font-outfit text-2xl font-black uppercase text-brand">INVOICE</h2>
                      <p className="text-xs text-neutral-500 mt-1">#{invoiceNumber || "___"}</p>
                      <p className="text-xs text-neutral-500">Date: {invoiceDate || today}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-8 mb-6 text-sm">
                    <div>
                      <h3 className="font-outfit font-bold uppercase text-xs tracking-wider border-b border-neutral-300 pb-1 mb-2">Bill To</h3>
                      <p className="font-semibold">{invClient.name || "________________________"}</p>
                      <p>{invClient.address || "________________________"}</p>
                      <p>{invClient.city || "________"}, {invClient.state} {invClient.zip || "_____"}</p>
                      <p>Phone: {invClient.phone || "________________________"}</p>
                    </div>
                    <div>
                      <h3 className="font-outfit font-bold uppercase text-xs tracking-wider border-b border-neutral-300 pb-1 mb-2">Payment Info</h3>
                      <p><span className="font-semibold">Invoice Date:</span> {invoiceDate || today}</p>
                      <p><span className="font-semibold">Due Date:</span> {dueDate || "Upon receipt"}</p>
                      <p className="mt-2 font-semibold text-brand">Late Fee: $100/day past due</p>
                    </div>
                  </div>
                  <table className="w-full mb-6 text-sm">
                    <thead>
                      <tr className="bg-brand-dark text-white text-xs">
                        <th className="text-left p-2 font-outfit font-bold uppercase tracking-wider">Description</th>
                        <th className="text-center p-2 w-16 font-outfit font-bold uppercase tracking-wider">Qty</th>
                        <th className="text-right p-2 w-24 font-outfit font-bold uppercase tracking-wider">Rate</th>
                        <th className="text-right p-2 w-24 font-outfit font-bold uppercase tracking-wider">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {invoiceItems.map((item, i) => (
                        <tr key={i} className="border-b border-neutral-200">
                          <td className="p-2">{item.description || "—"}</td>
                          <td className="p-2 text-center">{item.qty}</td>
                          <td className="p-2 text-right">${item.rate.toFixed(2)}</td>
                          <td className="p-2 text-right font-semibold">${(item.qty * item.rate).toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="border-t-2 border-black">
                        <td colSpan="3" className="p-2 text-right font-outfit font-bold uppercase">Total Due:</td>
                        <td className="p-2 text-right font-outfit font-bold text-lg">${invoiceSubtotal.toFixed(2)}</td>
                      </tr>
                    </tfoot>
                  </table>
                  {invoiceNotes && (
                    <div className="mb-6 p-4 bg-neutral-50 border border-neutral-200">
                      <h3 className="font-outfit font-bold uppercase text-xs tracking-wider mb-2">Notes</h3>
                      <p className="text-sm">{invoiceNotes}</p>
                    </div>
                  )}
                  <div className="border-2 border-brand p-3 text-center">
                    <p className="font-outfit font-bold text-brand text-sm uppercase tracking-wider">
                      Payment is due upon receipt. A late fee of $100.00 per day applies for each day past the due date.
                    </p>
                  </div>
                  <div className="mt-8 pt-4 border-t border-neutral-200 text-center text-xs text-neutral-500">
                    <p>Mid America Star Builders LLC — (901) 406-1803 — Masb254@yahoo.com — Memphis, TN</p>
                    <p className="font-semibold text-brand">Licensed & Insured — Financing Available</p>
                  </div>
                </div>
              </div>
            </TabsContent>
          </Tabs>

          {/* Bottom print button */}
          <div className="mt-8 text-center">
            <Button onClick={handlePrint} className="bg-brand text-white font-outfit font-bold uppercase tracking-wider text-sm px-10 py-6 hover:bg-brand-hover rounded-none">
              <Printer className="w-5 h-5 mr-2" /> Print / Save as PDF
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
